import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://app.tahc.ai';
import ReactMarkdown from 'react-markdown';
import {
  Search,
  ChevronRight,
  ArrowLeft,
  Book,
  Sparkles,
  LayoutDashboard,
  MessageSquare,
  Palette,
  Workflow,
  MessagesSquare
} from 'lucide-react';
import { helpSections, getArticleConfigBySlug, getAllArticleConfigs } from '../help/content';

// Declare window.tahc for TypeScript
declare global {
  interface Window {
    tahc?: {
      open: () => void;
      close: () => void;
      sendMessage: (msg: string) => void;
      reset: () => void;
      version: string;
    };
  }
}

// Map section IDs to icons
const sectionIcons: Record<string, React.ReactNode> = {
  'getting-started': <Sparkles className="w-5 h-5" />,
  'dashboard': <LayoutDashboard className="w-5 h-5" />,
  'widget': <MessageSquare className="w-5 h-5" />,
  'theming': <Palette className="w-5 h-5" />,
  'orchestrations': <Workflow className="w-5 h-5" />,
  'conversations': <MessagesSquare className="w-5 h-5" />,
};

export default function Docs() {
  const { slug } = useParams<{ slug?: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ReturnType<typeof getAllArticleConfigs>>([]);
  const [isSearching, setIsSearching] = useState(false);

  const articleConfig = slug ? getArticleConfigBySlug(slug) : null;
  const allArticles = getAllArticleConfigs();

  // Update document title for SEO
  useEffect(() => {
    if (articleConfig) {
      document.title = `${t(articleConfig.titleKey)} - Tahc Help`;
    } else {
      document.title = 'Tahc Documentation - Help & Guides';
    }

    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      if (articleConfig) {
        metaDescription.setAttribute('content', t(articleConfig.descriptionKey));
      } else {
        metaDescription.setAttribute('content', 'Tahc documentation and help guides. Learn how to set up and use the Tahc AI customer communication platform.');
      }
    }
  }, [articleConfig, t]);

  // Search functionality
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const query = searchQuery.toLowerCase();

    const results = allArticles.filter(article => {
      const title = t(article.titleKey).toLowerCase();
      const description = t(article.descriptionKey).toLowerCase();
      const content = t(article.contentKey).toLowerCase();

      return title.includes(query) || description.includes(query) || content.includes(query);
    });

    setSearchResults(results);
  }, [searchQuery, allArticles, t]);

  // Load widget on-demand and open it
  const handleContactSupport = () => {
    const scriptId = 'tahc-support-widget';
    const existingScript = document.getElementById(scriptId);

    // Helper to wait for widget to be ready and then open it
    const waitAndOpen = (maxAttempts = 20) => {
      let attempts = 0;
      const check = () => {
        attempts++;
        if (window.tahc) {
          window.tahc.open();
        } else if (attempts < maxAttempts) {
          setTimeout(check, 100);
        }
      };
      check();
    };

    if (existingScript && window.tahc) {
      // Widget already loaded, just open it
      window.tahc.open();
    } else if (!existingScript) {
      // Load widget script on-demand with hidden launcher
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = `${API_URL}/widget.js`;
      script.setAttribute('data-workspace-id', 'tahc-demo');
      script.setAttribute('data-start-hidden', 'true');
      script.async = true;
      script.onload = () => waitAndOpen();
      document.body.appendChild(script);
    } else {
      // Script exists but widget not ready yet
      waitAndOpen();
    }
  };

  // Get FAQs from translation (same approach as HelpCenter)
  const getFaqs = (article: ReturnType<typeof getArticleConfigBySlug>) => {
    if (!article?.faqKeys || article.faqKeys.length === 0) return [];

    const faqPrefix = article.faqKeys[0];
    const faqs: { question: string; answer: string }[] = [];

    // Try to get up to 10 FAQs
    for (let i = 1; i <= 10; i++) {
      const qKey = `${faqPrefix}.q${i}`;
      const aKey = `${faqPrefix}.a${i}`;
      const question = t(qKey);
      const answer = t(aKey);

      // Check if translation exists (doesn't return the key itself)
      if (question !== qKey && answer !== aKey) {
        faqs.push({ question, answer });
      } else {
        break;
      }
    }

    return faqs;
  };

  // Render article page
  if (articleConfig) {
    const content = t(articleConfig.contentKey);
    const faqs = getFaqs(articleConfig);

    const relatedArticles = articleConfig.relatedArticles
      ?.map((slug: string) => getArticleConfigBySlug(slug))
      .filter(Boolean) || [];

    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
            <Link to="/docs" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
              <Book className="w-6 h-6" />
              <span className="font-semibold text-lg">Tahc Docs</span>
            </Link>
            <div className="flex-1" />
            <Link
              to="/login"
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Sign in
            </Link>
          </div>
        </header>

        {/* Breadcrumb */}
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-4xl mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm">
              <Link to="/docs" className="text-gray-500 hover:text-gray-700">
                {t('help.title')}
              </Link>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <span className="text-gray-900">{t(articleConfig.titleKey)}</span>
            </nav>
          </div>
        </div>

        {/* Article Content */}
        <main className="max-w-4xl mx-auto px-4 py-8">
          <button
            onClick={() => navigate('/docs')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{t('help.title')}</span>
          </button>

          <article className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {t(articleConfig.titleKey)}
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              {t(articleConfig.descriptionKey)}
            </p>

            <div className="prose prose-gray max-w-none">
              <ReactMarkdown>{content}</ReactMarkdown>
            </div>
          </article>

          {/* FAQs */}
          {faqs.length > 0 && (
            <div className="mt-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {t('help.faqTitle')}
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <details
                    key={index}
                    className="bg-white rounded-lg border border-gray-200 overflow-hidden group"
                  >
                    <summary className="px-6 py-4 cursor-pointer font-medium text-gray-900 hover:bg-gray-50 list-none flex items-center justify-between">
                      {faq.question}
                      <ChevronRight className="w-5 h-5 text-gray-400 transform group-open:rotate-90 transition-transform" />
                    </summary>
                    <div className="px-6 py-4 border-t border-gray-100 text-gray-600">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          )}

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <div className="mt-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {t('help.relatedArticles')}
              </h2>
              <div className="grid gap-4">
                {relatedArticles.map(article => article && (
                  <Link
                    key={article.slug}
                    to={`/docs/${article.slug}`}
                    className="bg-white rounded-lg border border-gray-200 p-4 hover:border-blue-300 hover:shadow-sm transition"
                  >
                    <h3 className="font-medium text-gray-900">{t(article.titleKey)}</h3>
                    <p className="text-sm text-gray-500 mt-1">{t(article.descriptionKey)}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 mt-16">
          <div className="max-w-4xl mx-auto px-4 py-8 text-center text-gray-500 text-sm">
            <p>&copy; {new Date().getFullYear()} Tahc. All rights reserved.</p>
          </div>
        </footer>
      </div>
    );
  }

  // Render docs home / search results
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <div className="flex items-center gap-2 text-gray-900">
            <Book className="w-6 h-6" />
            <span className="font-semibold text-lg">Tahc Docs</span>
          </div>
          <div className="flex-1" />
          <Link
            to="/login"
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Sign in
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-b from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-4xl font-bold mb-4">
            {t('help.title')}
          </h1>
          <p className="text-xl text-blue-100 mb-8">
            Everything you need to get started and make the most of Tahc
          </p>

          {/* Search */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('help.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Search Results */}
        {isSearching && (
          <div className="mb-8">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {searchResults.length > 0
                ? `${t('help.resultsFor')} "${searchQuery}"`
                : t('help.noResults')
              }
            </h2>
            {searchResults.length > 0 ? (
              <div className="space-y-3">
                {searchResults.map(article => (
                  <Link
                    key={article.slug}
                    to={`/docs/${article.slug}`}
                    className="block bg-white rounded-lg border border-gray-200 p-4 hover:border-blue-300 hover:shadow-sm transition"
                  >
                    <h3 className="font-medium text-gray-900">{t(article.titleKey)}</h3>
                    <p className="text-sm text-gray-500 mt-1">{t(article.descriptionKey)}</p>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">{t('help.tryDifferent')}</p>
            )}
          </div>
        )}

        {/* Sections */}
        {!isSearching && (
          <div className="space-y-8">
            {helpSections.map(section => (
              <div key={section.id}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                    {sectionIcons[section.id]}
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {t(section.titleKey)}
                  </h2>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {section.articles.map(article => (
                    <Link
                      key={article.slug}
                      to={`/docs/${article.slug}`}
                      className="bg-white rounded-lg border border-gray-200 p-5 hover:border-blue-300 hover:shadow-sm transition group"
                    >
                      <h3 className="font-medium text-gray-900 group-hover:text-blue-600">
                        {t(article.titleKey)}
                      </h3>
                      <p className="text-sm text-gray-500 mt-2">
                        {t(article.descriptionKey)}
                      </p>
                      <div className="flex items-center gap-1 text-blue-600 text-sm mt-3 opacity-0 group-hover:opacity-100 transition">
                        Read more <ChevronRight className="w-4 h-4" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Need More Help */}
        <div className="mt-12 bg-gray-100 rounded-xl p-8 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {t('help.cantFind')}
          </h2>
          <p className="text-gray-600 mb-4">
            Our support team is here to help you with any questions.
          </p>
          <button
            onClick={handleContactSupport}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition"
          >
            <MessageSquare className="w-4 h-4" />
            {t('help.contactSupport')}
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-4xl mx-auto px-4 py-8 text-center text-gray-500 text-sm">
          <p>&copy; {new Date().getFullYear()} Tahc. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
